/**
 * OwenUnderwritingVerdict — replaces the legacy mocked AIUnderwritingFlow.
 *
 * Hybrid pipeline:
 *   1. Deterministic rules (eligibilityRules.js) run client-side, instantly.
 *   2. Score is computed locally from rules + readiness breakdown (scoring.js).
 *   3. The supabase function `owen-verdict` is invoked with the structured findings
 *      to generate Owen's narrative summary / strengths / conditions / risks.
 *      A rules-only fallback runs if the LLM is unavailable.
 *
 * The borrower sees the deterministic result immediately; the narrative
 * "fades in" once the edge function returns. Both paths are compliance-safe —
 * no "approved" language, no commitment to lend.
 */
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Surface, Button, Spinner } from '../../ui/components';
import {
  Sparkles, CheckCircle, AlertTriangle, XCircle,
  ArrowRight, RefreshCw, Lightbulb,
} from 'lucide-react';
import { evaluateEligibility } from '../../utils/owenUnderwriter/eligibilityRules';
import { calculateUnderwritingScore, tierFor } from '../../utils/owenUnderwriter/scoring';
import { completeUnderwriting, setCurrentStep, STEPS } from '../../store/loanApplicationSlice';
import { LK_FEATURES } from '../../config/features';
import { supabase } from '../../lib/supabase';
import ApplyLayout from '../layout/ApplyLayout';
import ForwardPacketCard from './ForwardPacketCard';

const STATUS_COLORS = {
  pass:      { bg: 'var(--lk-success-50)',  fg: 'var(--lk-success-700)',  border: 'var(--lk-success-300)',  Icon: CheckCircle },
  condition: { bg: 'var(--lk-warning-50)',  fg: 'var(--lk-warning-700)',  border: 'var(--lk-warning-300)',  Icon: AlertTriangle },
  fail:      { bg: 'var(--lk-danger-50)',   fg: 'var(--lk-danger-700)',   border: 'var(--lk-danger-300)',   Icon: XCircle },
};

const ScoreDial = ({ score, tier }) => {
  const arc = (score / 100) * 360;
  const color =
    tier?.color === 'success' ? '#16a34a' :
    tier?.color === 'warning' ? '#f59e0b' :
    '#dc2626';
  return (
    <div style={{
      width: 160, height: 160, position: 'relative',
      borderRadius: '50%',
      background: `conic-gradient(${color} 0deg, ${color} ${arc}deg, var(--lk-bg-surface-2) ${arc}deg)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        width: 130, height: 130, borderRadius: '50%',
        backgroundColor: 'var(--lk-bg-surface-1)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ fontSize: 36, fontWeight: 800, color: 'var(--lk-text-primary)', lineHeight: 1 }}>
          {score}
        </div>
        <div style={{ fontSize: 11, color: 'var(--lk-text-tertiary)' }}>/ 100</div>
        <div style={{
          marginTop: 6, fontSize: 11, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: 0.6, color,
          textAlign: 'center', maxWidth: 110,
        }}>
          {tier?.status}
        </div>
      </div>
    </div>
  );
};

const FindingRow = ({ finding }) => {
  const cfg = STATUS_COLORS[finding.status] || STATUS_COLORS.condition;
  const Icon = cfg.Icon;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: 12, marginBottom: 8,
      backgroundColor: cfg.bg, borderLeft: `3px solid ${cfg.border}`,
      borderRadius: 4,
    }}>
      <Icon size={18} color={cfg.fg} style={{ marginTop: 2, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: cfg.fg }}>{finding.title}</div>
        {finding.detail && (
          <div style={{ fontSize: 13, color: 'var(--lk-text-secondary)', marginTop: 2, lineHeight: 1.5 }}>
            {finding.detail}
          </div>
        )}
      </div>
    </div>
  );
};

const Bullets = ({ items, icon: Icon, iconColor, label }) => {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <h6 style={{
        fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.6, color: 'var(--lk-text-tertiary)', marginBottom: 8,
      }}>
        {label}
      </h6>
      <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
        {items.map((it, i) => (
          <li key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'flex-start' }}>
            <Icon size={14} color={iconColor} style={{ marginTop: 4, flexShrink: 0 }} />
            <span style={{ fontSize: 14, color: 'var(--lk-text-primary)', lineHeight: 1.5 }}>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const OwenUnderwritingVerdict = () => {
  const dispatch = useDispatch();
  const state = useSelector((s) => s.loanApplication);
  const recommendedProduct = state?.recommendedProduct;

  const [analyzing, setAnalyzing] = useState(true);
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [narrativeError, setNarrativeError] = useState(null);

  // Deterministic eligibility + score (instant).
  const { eligibility, scoreResult } = useMemo(() => {
    const elig = evaluateEligibility(state);
    const score = calculateUnderwritingScore(state, elig);
    return { eligibility: elig, scoreResult: score };
  }, [state]);

  // Brief "Owen is reviewing" beat so the verdict feels considered, not magical.
  useEffect(() => {
    const t = setTimeout(() => setAnalyzing(false), 1200);
    return () => clearTimeout(t);
  }, []);

  // Persist the score into the slice so other components see it.
  useEffect(() => {
    if (!analyzing && scoreResult?.score !== undefined) {
      dispatch(completeUnderwriting({
        score: scoreResult.score,
        tier: scoreResult.tier?.status,
        completedAt: new Date().toISOString(),
      }));
    }
  }, [analyzing, scoreResult?.score, scoreResult?.tier?.status, dispatch]);

  // Kick off the narrative LLM call once analysis is "done".
  useEffect(() => {
    if (analyzing) return;
    let cancelled = false;
    (async () => {
      setNarrativeLoading(true);
      setNarrativeError(null);
      try {
        const ctx = eligibility?.context || {};
        const payload = {
          score: scoreResult.score,
          tier: scoreResult.tier?.status,
          findings: eligibility.findings,
          program: {
            name: recommendedProduct?.name,
            displayName: recommendedProduct?.displayName,
            productFamily: recommendedProduct?.productFamily,
            qualifyingMethod: recommendedProduct?.qualifyingMethod,
          },
          borrower: {
            loanPurpose: ctx.purpose,
            propertyValue: ctx.propertyValue,
            loanAmount: ctx.loanAmount,
            ltv: ctx.ltv,
            creditRange: state?.productRecommendation?.creditScore,
            employmentStatus: state?.productRecommendation?.employmentStatus,
            occupancy: state?.productRecommendation?.propertyUse,
          },
        };
        const { data, error } = await supabase.functions.invoke('owen-verdict', { body: payload });
        if (error) throw error;
        if (!cancelled) setNarrative(data);
      } catch (e) {
        console.warn('owen-verdict failed:', e?.message);
        if (!cancelled) {
          // Synthesize a minimal narrative from findings so the UI always has something.
          setNarrativeError(e?.message || 'Owen is offline');
          setNarrative(synthesizeFromFindings(eligibility, scoreResult, recommendedProduct));
        }
      } finally {
        if (!cancelled) setNarrativeLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [analyzing, eligibility, scoreResult, recommendedProduct, state]);

  const goNext = () => {
    dispatch(setCurrentStep(LK_FEATURES.lenderMarketplace ? STEPS.LENDER_NETWORK : STEPS.UNDERWRITING));
  };

  if (analyzing) {
    return (
      <ApplyLayout step={4} totalSteps={5} title="Owen is reviewing your file…" subtitle="Running program eligibility, capacity, collateral, and capital checks." context="ai-underwriting">
        <Surface padding="var(--lk-space-8)" style={{ textAlign: 'center' }}>
          <Spinner size="lg" />
          <p style={{ marginTop: 16, color: 'var(--lk-text-secondary)' }}>
            Owen is comparing your stated profile against {recommendedProduct?.displayName || 'your recommended program'}'s rules.
          </p>
        </Surface>
      </ApplyLayout>
    );
  }

  const tier = scoreResult?.tier || tierFor(scoreResult?.score || 0);
  const conditionFindings = eligibility.findings.filter(f => f.status === 'condition');
  const failFindings = eligibility.findings.filter(f => f.status === 'fail');
  const passFindings = eligibility.findings.filter(f => f.status === 'pass');

  return (
    <ApplyLayout
      step={4}
      totalSteps={5}
      title="Owen's Underwriting Verdict"
      subtitle={`Reviewed against ${recommendedProduct?.displayName || recommendedProduct?.name || 'your program'} · stated values${(state.documents || []).length > 0 ? ' + uploaded documents' : ''}`}
      context="ai-underwriting"
    >
      {/* Score + summary */}
      <Surface padding="var(--lk-space-6)" style={{
        display: 'flex', gap: 24, alignItems: 'center',
        flexWrap: 'wrap', marginBottom: 'var(--lk-space-5)',
      }}>
        <ScoreDial score={scoreResult.score} tier={tier} />
        <div style={{ flex: 1, minWidth: 280 }}>
          <h3 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>
            {tier.status}
          </h3>
          {narrativeLoading && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 14, color: 'var(--lk-text-tertiary)', marginBottom: 8,
            }}>
              <Spinner size="sm" /> Owen is composing your verdict…
            </div>
          )}
          {narrative?.summary && (
            <p style={{ fontSize: 15, color: 'var(--lk-text-secondary)', lineHeight: 1.6, margin: 0 }}>
              {narrative.summary}
            </p>
          )}
          {narrativeError && narrative?.model === 'rules-fallback' && (
            <div style={{
              marginTop: 10, fontSize: 12, color: 'var(--lk-text-tertiary)',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <RefreshCw size={11} />
              <span>Owen's narrative composer is offline; showing the deterministic verdict only.</span>
            </div>
          )}
        </div>
      </Surface>

      {/* Score composition */}
      <Surface padding="var(--lk-space-4) var(--lk-space-5)" style={{ marginBottom: 'var(--lk-space-5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <ScoreBreakdownChip
            label="Program Eligibility"
            value={scoreResult.breakdown.eligibilityScore}
            max={40}
            pct={scoreResult.breakdown.eligibilityPct}
          />
          <ScoreBreakdownChip
            label="Mortgage Readiness"
            value={scoreResult.breakdown.readinessScore}
            max={60}
            pct={scoreResult.breakdown.readinessPct}
          />
        </div>
      </Surface>

      {/* Findings */}
      <h5 style={{ fontWeight: 700, marginBottom: 12 }}>Owen's Findings</h5>

      {failFindings.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <h6 style={{
            fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 0.6, color: 'var(--lk-danger-600)', marginBottom: 8,
          }}>
            Blockers
          </h6>
          {failFindings.map((f) => <FindingRow key={f.id} finding={f} />)}
        </div>
      )}

      {conditionFindings.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <h6 style={{
            fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 0.6, color: 'var(--lk-warning-600)', marginBottom: 8,
          }}>
            Conditions to clear
          </h6>
          {conditionFindings.map((f) => <FindingRow key={f.id} finding={f} />)}
        </div>
      )}

      {passFindings.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <h6 style={{
            fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 0.6, color: 'var(--lk-success-600)', marginBottom: 8,
          }}>
            Looks good
          </h6>
          {passFindings.map((f) => <FindingRow key={f.id} finding={f} />)}
        </div>
      )}

      {/* Owen's narrative bullets */}
      {(narrative?.recommendations?.length > 0 || narrative?.risks?.length > 0) && (
        <Surface padding="var(--lk-space-5)" style={{
          marginTop: 'var(--lk-space-5)',
          background: 'linear-gradient(135deg, var(--lk-primary-50), var(--lk-bg-surface-2))',
          border: '1px solid var(--lk-primary-200)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Sparkles size={18} color="var(--lk-primary-600)" />
            <h5 style={{ margin: 0, fontWeight: 700 }}>Owen's Take</h5>
          </div>
          <Bullets
            items={narrative.recommendations}
            icon={Lightbulb}
            iconColor="var(--lk-primary-600)"
            label="Recommended Next Steps"
          />
          <Bullets
            items={narrative.risks}
            icon={AlertTriangle}
            iconColor="var(--lk-warning-600)"
            label="Things to Watch"
          />
        </Surface>
      )}

      {/* Forward Packet — full pre-underwritten file */}
      <ForwardPacketCard
        packetVersion="underwriting"
        verdict={{
          summary: narrative?.summary,
          findings: eligibility.findings,
          recommendations: narrative?.recommendations || [],
        }}
        documentChecklist={(state.documents || []).map((d) => ({
          name: d.docName || d.name,
          uploaded: true,
        }))}
      />

      {LK_FEATURES.lenderMarketplace && (
        <div style={{
          display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--lk-space-6)',
        }}>
          <Button variant="primary" size="lg" icon={ArrowRight} onClick={goNext}>
            Continue to Lender Network
          </Button>
        </div>
      )}
    </ApplyLayout>
  );
};

const ScoreBreakdownChip = ({ label, value, max, pct }) => (
  <div style={{ flex: 1, minWidth: 200 }}>
    <div style={{
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: 0.6, color: 'var(--lk-text-tertiary)', marginBottom: 6,
    }}>
      {label}
    </div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 6 }}>
      <span style={{ fontSize: 24, fontWeight: 800, color: 'var(--lk-text-primary)' }}>{value}</span>
      <span style={{ fontSize: 13, color: 'var(--lk-text-tertiary)' }}>/ {max}</span>
      <span style={{
        marginLeft: 'auto', fontSize: 12, color: 'var(--lk-text-tertiary)',
      }}>
        {pct}% of available
      </span>
    </div>
    <div style={{
      height: 6, width: '100%', backgroundColor: 'var(--lk-bg-surface-2)', borderRadius: 3, overflow: 'hidden',
    }}>
      <div style={{
        height: 6, width: `${(value / max) * 100}%`,
        backgroundColor: 'var(--lk-primary-500)',
      }} />
    </div>
  </div>
);

function synthesizeFromFindings(elig, scoreResult, product) {
  const passes = elig.findings.filter(f => f.status === 'pass');
  const conds = elig.findings.filter(f => f.status === 'condition');
  const fails = elig.findings.filter(f => f.status === 'fail');
  const programName = product?.displayName || product?.name || 'this program';
  const tierLabel = scoreResult?.tier?.status || 'reviewed';
  const summary = fails.length
    ? `Owen reviewed the file against ${programName}. ${fails.length} item${fails.length === 1 ? '' : 's'} would block this specific program — Owen recommends discussing alternatives.`
    : `Owen reviewed the file against ${programName}. Score is ${scoreResult?.score || 0}/100 (${tierLabel}). ${conds.length === 0 ? 'All program rules look satisfied based on stated information.' : `${conds.length} item${conds.length === 1 ? '' : 's'} need${conds.length === 1 ? 's' : ''} clarification before submission.`}`;
  return {
    summary,
    strengths: passes.map(f => f.title).slice(0, 4),
    conditions: conds.map(f => f.title).slice(0, 5),
    risks: fails.map(f => f.title).slice(0, 3),
    recommendations: [
      'Forward this packet to your loan officer to formally start the process',
      conds.length > 0 ? 'Gather documentation to clear the conditions above' : null,
      fails.length > 0 ? 'Discuss alternative loan programs with your loan officer' : null,
      (passes.length < elig.findings.length) ? 'Pull credit and verify income to convert "stated" to "documented"' : null,
    ].filter(Boolean).slice(0, 4),
    model: 'rules-fallback',
  };
}

export default OwenUnderwritingVerdict;
